(ns ui.view.transaction-process.components.sub-entity-display
  "Read-only renderers for a sub-entity's (decision / attestation / declaration)
   contents: its value-field rows, conditions, participants, investment reservations
   and attachments. Shared by the form's peek panel (`detail_panel`) and the
   read-only Transaction details modal so the two surfaces can't drift. All field
   lists derive from `tp-util/slot-display-fields` / the template layout."
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :refer [tr]]
   [frontend.utils :as fu]
   [re-frame.core :as rf]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.util :as tp-util]))

(defn- field-display
  [{:keys [label value]}]
  [:div {:class "mb-3"}
   [:span {:class "text-xs m-0 font-medium text-gray-500 block"} label]
   [:span {:class "text-sm m-0 text-gray-900"}
    (cond
      (nil? value) "-"
      (keyword? value) [tp-util/render-error {:context (str "field-display received raw keyword for label \"" label "\"")
                                              :value value}]
      :else value)]])

(defn- notes-block
  [{:keys [label value]}]
  [:div {:class "mb-4"}
   [:span {:class "text-xs m-0 font-medium text-gray-500 block mb-1"} label]
   [:p {:class "text-sm m-0 text-gray-700 whitespace-pre-wrap"} value]])

(defn value-field-rows
  "Read-only rows for a sub-entity's display fields (date + free-text), derived
  from the slot's `slot-display-fields` so the peek, details modal, and PDF all
  share one field list and can't drift. Values format through `format-field-value`;
  date fields always render (blank → '-'), other fields only when present;
  multiline text renders in a pre-wrap block."
  [{:keys [slot-template category entity template]}]
  (let [field-configs (:template/fields template)]
    (->> (tp-util/slot-display-fields {:slot-template slot-template :category category})
         (keep (fn [fk]
                 (let [fc        (get field-configs fk)
                       data-type (:field/data-type fc)
                       label     (tp-util/field-label fk fc)
                       value     (tp-util/format-field-value
                                  {:field-kw fk :field-cfg fc :entity-data entity})
                       present?  (some? (get entity fk))]
                   (cond
                     (= data-type :data.type/text-multiline)
                     (when present? ^{:key fk} [notes-block {:label label :value value}])

                     (or present? (= data-type :data.type/date))
                     ^{:key fk} [field-display {:label label :value value}]))))
         doall)))

(defn conditions-list
  "`show-status?` controls the met/not-met affordance. Decision conditions are
  status-less (CapMan: only title + description), so the decision panel passes
  false; verification (attestation) conditions keep their met/not-met status.
  `show-label?` defaults to true; summary cards can hide the repeated Conditions
  heading when the source entry is already labelled."
  [{:keys [entity conditions-field can-write? show-status? show-label?]
    :or {show-label? true}}]
  (when-let [condition-refs (get entity conditions-field)]
    (let [conditions (->> condition-refs
                          (map (fn [ref]
                                 @(rf/subscribe [::session-sub/entity (or (:db/id ref) ref)])))
                          (remove nil?)
                          (remove :common/disabled?)
                          (sort-by #(or (:common/ord %) 0)))]
      (when (seq conditions)
        [:div {:class (when show-label? "mb-4")}
         (when show-label?
           [:span {:class "text-xs m-0 font-medium text-gray-500 block mb-2"} (tr :conditions)])
         [:div {:class "flex flex-col gap-2"}
          (doall
           (for [[idx c] (map-indexed vector conditions)]
             (let [{:keys [title-kw description-kw status-kw status-true]} (tp-util/condition-attrs c)
                   met? (and show-status? (= (get c status-kw) status-true))]
               ^{:key (:db/id c)}
               [:div {:class "border border-gray-200 rounded-lg p-3"}
                [:div {:class "flex items-center justify-between mb-1"}
                 [:div {:class "flex items-center gap-2"}
                  [:span {:class (uu/cm "w-5 h-5 rounded-full flex items-center justify-center text-xs m-0 shrink-0"
                                        (if met?
                                          "bg-primary-regular text-white"
                                          "bg-gray-200 text-gray-500"))}
                   (if met? [:i {:class "fal fa-check"}] (str (inc idx)))]
                  [:span {:class "text-sm m-0 font-medium text-gray-800"}
                   (or (get c title-kw) (tr :transaction-process/condition-n [(inc idx)]))]]
                 (when show-status?
                   (if (and can-write? (not met?))
                     [:button {:class    (uu/cm "px-2.5 py-1 text-xs m-0 rounded border"
                                                "border-gray-300 text-gray-600 hover:bg-gray-50")
                               :on-click (fn []
                                           (rf/dispatch [::event/mark-condition-as-met
                                                         (:db/id c)
                                                         status-kw]))}
                      (tr :mark-as-met)]
                     [:span {:class (uu/cm "px-2.5 py-1 text-xs m-0 rounded border"
                                           (if met?
                                             "border-primary-regular/30 text-primary-regular-text-accent bg-primary-regular/10"
                                             "border-gray-300 text-gray-600"))}
                      (if met? (tr :met) (tr :not-met))]))]
                (when-let [desc (get c description-kw)]
                  [:p {:class "text-xs m-0 text-gray-500 mt-1 line-clamp-4"} desc])])))]]))))

(defn- kv-row
  "Label-over-value row used inside the sub-entity cards. `:clamp?` truncates the
  value to one line (for multiline fields like notes)."
  [{:keys [label value clamp?]}]
  [:div {:class "flex flex-col"}
   [:span {:class "text-xs m-0 font-medium text-gray-500"} label]
   [:span {:class (uu/cm "text-sm m-0 text-gray-800" (when clamp? "line-clamp-1"))}
    value]])

(defn- participant-card
  "Person name as header, then the populated fields the modal exposes (attendance,
  role, notes). Field labels resolve from the template field-configs (`field-label`)
  — the same source the modal uses — so the read view can't drift from the edit form."
  [{:keys [participant field-configs id->entity]}]
  (let [person-name (tp-util/materialize-field {:field      :decision-participant/person
                                                :entity     participant
                                                :id->entity id->entity})
        attendance  (tp-util/materialize-field {:field      :decision-participant/attendance
                                                :entity     participant
                                                :id->entity id->entity})
        role-names  (tp-util/ref-multi-labels {:field      :decision-participant/role
                                               :entity     participant
                                               :id->entity id->entity})
        notes       (:decision-participant/notes participant)
        label-for   (fn [fk] (tp-util/field-label fk (get field-configs fk)))]
    [:div {:class "border border-gray-200 rounded-lg p-3"}
     [:div {:class "text-sm m-0 font-semibold text-gray-900 mb-2"}
      (or person-name (tr :unknown))]
     (into
      [:div {:class "flex flex-col gap-2"}]
      (remove nil?
              [(when-not (string/blank? (str attendance))
                 (kv-row {:label (label-for :decision-participant/attendance) :value attendance}))
               (when (seq role-names)
                 (kv-row {:label (label-for :decision-participant/role) :value (string/join ", " role-names)}))
               (when-not (string/blank? notes)
                 (kv-row {:label (label-for :decision-participant/notes) :value notes :clamp? true}))]))]))

(defn participants-list
  [{:keys [decision field-configs]}]
  (let [participants (->> (tp-util/get-sub-entities decision :decision/participants)
                          ;; Match the modal's ordering (decision sub sorts by :common/ord).
                          (sort-by #(or (:common/ord %) 0)))
        id->entity   @(rf/subscribe [::session-sub/legacy-entities])]
    (when (seq participants)
      [:div {:class "mb-4"}
       [:span {:class "text-xs m-0 font-medium text-gray-500 block mb-2"} (tr :participants)]
       [:div {:class "flex flex-col gap-2"}
        (doall
         (for [p participants]
           ^{:key (:db/id p)}
           [participant-card {:participant   p
                              :field-configs field-configs
                              :id->entity    id->entity}]))]])))

(defn- reservation-card
  "Renders one reservation read-only, driven by the same template layout and
  reservation-type enabled-fields the editing modal uses (via
  `tp-util/reservation-display-fields`) so the read view can't drift from the modal.
  Only populated fields are shown; multiline fields clamp to one line."
  [{:keys [reservation template id->entity]}]
  (let [type-ref    (:investment-reservation/type reservation)
        type-id     (or (:db/id type-ref) type-ref)
        type-entity (when type-id @(rf/subscribe [::session-sub/entity type-id]))
        title       (or (:investment-reservation-type/title type-entity)
                        (when type-entity (entity/display-string type-entity)))
        enabled     (:investment-reservation-type/enabled-fields type-entity)
        enabled?    (fn [attribute] (or (nil? enabled) (contains? (set enabled) attribute)))
        fields      (tp-util/reservation-display-fields template enabled?)]
    [:div {:class "border border-gray-200 rounded-lg p-3"}
     (when title
       [:div {:class "text-sm m-0 font-semibold text-gray-900 mb-2"} title])
     (into
      [:div {:class "flex flex-col gap-2"}]
      (->> fields
           (keep (fn [{:field/keys [attribute label data-type]}]
                   (let [value (tp-util/materialize-field {:field      attribute
                                                           :entity     reservation
                                                           :id->entity id->entity})]
                     (when-not (string/blank? (str value))
                       (kv-row {:label  label
                                :value  value
                                :clamp? (= data-type :data.type/text-multiline)})))))
           doall))]))

(defn reservations-list
  [{:keys [template decision]}]
  (let [reservations (->> (tp-util/get-sub-entities decision :decision/investment-reservations)
                          ;; Match the modal's ordering (decision sub sorts by
                          ;; :common/ord, default 0) so the read view lists them the same.
                          (sort-by #(or (:common/ord %) 0)))
        id->entity   @(rf/subscribe [::session-sub/legacy-entities])]
    (when (seq reservations)
      [:div {:class "mb-4"}
       [:span {:class "text-xs m-0 font-medium text-gray-500 block mb-2"} (tr :transaction-process/investment-reservations)]
       [:div {:class "flex flex-col gap-2"}
        (doall
         (for [res reservations]
           ^{:key (:db/id res)}
           [reservation-card {:reservation res
                              :template    template
                              :id->entity  id->entity}]))]])))

(defn attachments-list
  [entity]
  (when-let [att-refs (:common/attachments entity)]
    (let [attachments (->> att-refs
                           (map (fn [ref] @(rf/subscribe [::session-sub/entity (or (:db/id ref) ref)])))
                           (remove nil?)
                           (remove :common/disabled?)
                           (sort-by :attachment/uploaded-on))]
      (when (seq attachments)
        [:div {:class "mb-4"}
         [:span {:class "text-xs m-0 font-medium text-gray-500 block mb-2"} (tr :documents)]
         [:div {:class "flex flex-col gap-2"}
          (doall
           (for [att attachments]
             ^{:key (:db/id att)}
             [:button {:class    "flex items-center gap-2 text-sm m-0 text-gray-700 hover:text-primary-regular-text-accent p-0"
                       :on-click #(fu/open-attachment! (:db/id att))}
              [:i {:class "fal fa-file text-gray-400"}]
              [:span
               {:class "text-start"}
               (:attachment/filename att)]]))]]))))
